import { NAVIGATION_HEADER_TITLES, NAVIGATION_PATHS } from "@/const/navigation";
import { isVercelPreviewHostname, orgRootUrl } from "./orgHostRouting";

const rootDomainEnv = process.env.NEXT_PUBLIC_ROOT_DOMAIN;
export const ROOT_DOMAIN =
  !rootDomainEnv || rootDomainEnv === "localhost"
    ? "localhost:3000"
    : rootDomainEnv;

export const LINK_DOMAIN =
  process.env.NEXT_PUBLIC_LINK_DOMAIN || "godetour.link";

// URLs rendered into HTML must pass an origin (see `requestOrigin.ts`). The
// `window` fallback gives different URLs on server and client.
export type UrlOrigin = { host: string; protocol: string };

function resolveOrigin(origin?: UrlOrigin): UrlOrigin | null {
  if (origin) return origin;
  if (typeof window === "undefined") return null;
  return { host: window.location.host, protocol: window.location.protocol };
}

export function buildOrgUrl(
  subdomain: string,
  path: string,
  origin?: UrlOrigin
): string {
  const resolved = resolveOrigin(origin);
  if (!resolved) return "";
  // The root ends in "/", and `path` starts with one.
  return `${orgRootUrl(resolved, subdomain).slice(0, -1)}${path}`;
}

/**
 * Returns true when running in a Vercel preview deployment that is NOT a
 * subdomain of the configured ROOT_DOMAIN (e.g. godetour.app).
 * Those deployments have no wildcard subdomain support, so all org navigation
 * must use path-based routing (/subdomain/<org>/) instead.
 */
export function isVercelPreviewDeploy(origin?: UrlOrigin): boolean {
  const resolved = resolveOrigin(origin);
  if (!resolved) return false;
  return isVercelPreviewHostname(resolved.host.split(":")[0]);
}

/**
 * Navigates to an org's root via a full browser navigation (window.location.href).
 *
 * Why window.location.href and not router.push/replace:
 * - router.* is same-origin only — cross-subdomain URLs are silently dropped or
 *   fall through to window.location.assign in ways that vary across Next.js versions.
 * - A full navigation is intentional here: it re-runs middleware, clears the
 *   Zustand store, and re-fetches org data fresh — exactly what you want after
 *   creating, deleting, or leaving an org.
 */
/**
 * Any same-origin path, for redirects that are not tied to one flow.
 *
 * "starts with /" is not enough on its own: "//evil.example" and
 * "/\evil.example" pass it and browsers resolve both as another origin.
 */
export function safeInternalPath(value: string | null): string | null {
  if (!value || !value.startsWith("/")) {
    return null;
  }
  return /^\/[/\\]/.test(value) ? null : value;
}

/**
 * Allowlist, not a "starts with /" check: that still accepts "//evil.example"
 * and "/\evil.example", which browsers resolve as another origin.
 */
export function safeCliReturnPath(value: string | null): string | null {
  if (!value) {
    return null;
  }
  return value.startsWith(`${NAVIGATION_PATHS.CliAuthorize}?`) ? value : null;
}

export function navigateToOrg(subdomain: string): void {
  window.location.href = orgRootUrl(window.location, subdomain);
}

export function buildMainDomainUrl(path: string, origin?: UrlOrigin): string {
  const resolved = resolveOrigin(origin);

  // Vercel preview has no wildcard subdomain, so its host is the main domain.
  if (resolved && isVercelPreviewDeploy(resolved)) {
    return `${resolved.protocol}//${resolved.host}${path}`;
  }
  const protocol =
    resolved?.protocol ??
    (ROOT_DOMAIN.startsWith("localhost") ? "http:" : "https:");
  return `${protocol}//${ROOT_DOMAIN}${path}`;
}

/**
 * Builds the absolute, main-domain, path-based billing URL.
 *
 * Why: Paddle only verifies the main domain as a checkout origin. Org subdomains
 * are unverified, so the billing page must initialize Paddle on the main host.
 */
export function buildMainDomainBillingUrl(
  subdomain: string,
  origin?: UrlOrigin
): string {
  return buildMainDomainUrl(`/subdomain/${subdomain}/billing`, origin);
}

export function getNavigationTitle(pathname: string) {
  const normalizedPath = pathname.replace(
    /^\/subdomain\/[^/]+/,
    ""
  ) as NAVIGATION_PATHS;

  if (normalizedPath.includes(NAVIGATION_PATHS.Applications)) {
    return NAVIGATION_HEADER_TITLES[NAVIGATION_PATHS.Applications];
  }

  // Detail pages (an import job, a named event) are not in the map, and
  // falling through to "Dashboard" says the user left a section they are
  // still in.
  if (normalizedPath.startsWith("/analytics")) {
    return NAVIGATION_HEADER_TITLES[NAVIGATION_PATHS.AnalyticsOverview];
  }

  for (const section of [
    NAVIGATION_PATHS.Experiments,
    NAVIGATION_PATHS.FeatureFlags,
  ]) {
    if (normalizedPath.startsWith(`${section}/`)) {
      return NAVIGATION_HEADER_TITLES[section];
    }
  }

  return NAVIGATION_HEADER_TITLES[normalizedPath] ?? "Dashboard";
}
